package br.com.vvrefeicoes.portal.feature.auth.forgotPassword

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.unit.dp
import br.com.vvrefeicoes.portal.core.ui.components.AppFooter
import br.com.vvrefeicoes.portal.core.ui.components.AppLogo
import br.com.vvrefeicoes.portal.core.ui.layout.AppResponsiveLayout
import br.com.vvrefeicoes.portal.core.ui.layout.AppScrollColumn
import br.com.vvrefeicoes.portal.core.ui.theme.AppSpacing
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource
import portalvv.shared.generated.resources.Res
import portalvv.shared.generated.resources.slogan
import portalvv.shared.generated.resources.undrawable_authentication
import portalvv.shared.generated.resources.undrawable_forgot_password
import portalvv.shared.generated.resources.undrawable_secure_password

private object ForgotPasswordLayoutDefaults {
    val formMaxWidth = 420.dp
    val illustrationAspectRatio = 480f / 354f
}

@Composable
actual fun ForgotPasswordLayout(controller: ForgotPasswordController) {
    AppResponsiveLayout(modifier = Modifier.fillMaxSize()) { isCompact ->
        if (isCompact) {
            CompactForgotPasswordLayout(controller)
        } else {
            WideForgotPasswordLayout(controller)
        }
    }
}

@Composable
private fun CompactForgotPasswordLayout(controller: ForgotPasswordController) {
    AppScrollColumn(
        modifier = Modifier.fillMaxSize(),
        contentPadding = PaddingValues(AppSpacing.space24),
        verticalArrangement = Arrangement.spacedBy(AppSpacing.space24),
        horizontalAlignment = Alignment.CenterHorizontally,
        footer = { AppFooter() },
    ) {
        Column(
            modifier =
                Modifier
                    .widthIn(max = ForgotPasswordLayoutDefaults.formMaxWidth)
                    .fillMaxWidth(),
            verticalArrangement = Arrangement.spacedBy(AppSpacing.space24),
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            AppLogo()
            Text(
                text = stringResource(Res.string.slogan),
                style = MaterialTheme.typography.titleLarge,
                color = MaterialTheme.colorScheme.onBackground,
            )
            Illustration(
                step = controller.step,
                modifier =
                    Modifier
                        .widthIn(max = 298.dp)
                        .fillMaxWidth(0.9f),
            )
            ForgotPasswordForm(controller = controller)
        }
    }
}

@Composable
private fun WideForgotPasswordLayout(controller: ForgotPasswordController) {
    BoxWithConstraints(modifier = Modifier.fillMaxSize()) {
        val minHeight = maxHeight
        AppScrollColumn(modifier = Modifier.fillMaxSize()) {
            Row(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .heightIn(min = minHeight),
            ) {
                Column(
                    modifier =
                        Modifier
                            .weight(1f)
                            .heightIn(min = minHeight)
                            .padding(vertical = AppSpacing.space32, horizontal = AppSpacing.space48),
                    verticalArrangement = Arrangement.Top,
                    horizontalAlignment = Alignment.Start,
                ) {
                    ForgotPasswordForm(controller = controller)
                }

                Column(
                    modifier =
                        Modifier
                            .weight(1f)
                            .heightIn(min = minHeight)
                            .background(MaterialTheme.colorScheme.background)
                            .padding(start = AppSpacing.space32, top = AppSpacing.space32, end = AppSpacing.space32),
                    verticalArrangement = Arrangement.SpaceBetween,
                    horizontalAlignment = Alignment.CenterHorizontally,
                ) {
                    Column(
                        verticalArrangement = Arrangement.spacedBy(AppSpacing.space16),
                        horizontalAlignment = Alignment.CenterHorizontally,
                    ) {
                        AppLogo()
                        Text(
                            text = stringResource(Res.string.slogan),
                            style = MaterialTheme.typography.titleLarge,
                            color = MaterialTheme.colorScheme.onBackground,
                        )
                    }
                    Illustration(
                        step = controller.step,
                        modifier =
                            Modifier
                                .widthIn(max = 480.dp)
                                .fillMaxWidth(0.9f),
                    )
                    AppFooter()
                }
            }
        }
    }
}

@Composable
private fun Illustration(
    step: ForgotPasswordStep,
    modifier: Modifier = Modifier,
) {
    Image(
        painter = painterResource(illustrationForStep(step)),
        contentDescription = null,
        modifier = modifier.aspectRatio(ForgotPasswordLayoutDefaults.illustrationAspectRatio),
        contentScale = ContentScale.Fit,
    )
}

private fun illustrationForStep(step: ForgotPasswordStep): DrawableResource =
    when (step) {
        ForgotPasswordStep.Email -> Res.drawable.undrawable_authentication
        ForgotPasswordStep.Code -> Res.drawable.undrawable_forgot_password
        ForgotPasswordStep.Password -> Res.drawable.undrawable_secure_password
    }
